/**
 * Single status -> tone map (UI density Phase 1, AB#4531).
 *
 * Every status-coloured surface (Badge, StatusBadge, report/analysis status
 * cells, inline status messages) classifies a status string here and styles
 * it with the `--status-*` colour roles from `app/globals.css`. Do not add
 * new per-component status -> colour maps: add generic statuses to
 * `TONE_STATUSES`, and map domain-specific states (effective status, share
 * link state, ...) to a `StatusTone`, then style them with
 * `STATUS_TONE_CLASSES`.
 */

export type StatusTone =
  "success" | "warning" | "error" | "info" | "scheduled" | "neutral";

export interface StatusToneClasses {
  /** Text colour (meets WCAG AA on white and on the tone's own tint). */
  readonly text: string;
  /** Tinted background. */
  readonly bg: string;
  /** Subtle container border. */
  readonly border: string;
  /** Strong accent for left rules / indicators (non-text). */
  readonly accent: string;
  /** Icon colour. */
  readonly icon: string;
}

/** Status strings (lower-case, `_` or space separated) per tone. */
const TONE_STATUSES: Record<
  Exclude<StatusTone, "neutral">,
  readonly string[]
> = {
  success: [
    "completed",
    "success",
    "active",
    "accepted",
    "approved",
    "in use",
    "in_use",
    "processed",
  ],
  info: [
    "processing",
    "in_progress",
    "inference_complete",
    "post_processing",
    "running",
    "syncing",
  ],
  scheduled: ["scheduled", "invited", "sent", "sign_off_requested"],
  warning: ["pending", "queued", "waiting", "draft"],
  error: ["failed", "error", "cancelled", "rejected", "expired", "deleted"],
};

const STATUS_TO_TONE: ReadonlyMap<string, StatusTone> = new Map(
  (Object.entries(TONE_STATUSES) as [StatusTone, readonly string[]][]).flatMap(
    ([tone, statuses]) => statuses.map((s) => [s, tone] as const),
  ),
);

/**
 * Classify a status string. Unknown, empty and archive-type statuses
 * (archived, inactive, not in use) are `neutral`.
 */
export function statusTone(status: string | null | undefined): StatusTone {
  if (!status) return "neutral";
  const key = status.trim().toLowerCase();
  return (
    STATUS_TO_TONE.get(key) ??
    STATUS_TO_TONE.get(key.replaceAll(" ", "_")) ??
    "neutral"
  );
}

/** Tailwind classes per tone, built on the `--status-*` colour roles. */
export const STATUS_TONE_CLASSES: Record<StatusTone, StatusToneClasses> = {
  success: {
    text: "text-status-success-text",
    bg: "bg-status-success-bg",
    border: "border-status-success-border/30",
    accent: "border-l-status-success-border",
    icon: "text-status-success-text",
  },
  warning: {
    text: "text-status-warning-text",
    bg: "bg-status-warning-bg",
    border: "border-status-warning-border/30",
    accent: "border-l-status-warning-border",
    icon: "text-status-warning-text",
  },
  error: {
    text: "text-status-error-text",
    bg: "bg-status-error-bg",
    border: "border-status-error-border/30",
    accent: "border-l-status-error-border",
    icon: "text-status-error-text",
  },
  info: {
    text: "text-status-info-text",
    bg: "bg-status-info-bg",
    border: "border-status-info-border/30",
    accent: "border-l-status-info-border",
    icon: "text-status-info-text",
  },
  // No --status-* role for "scheduled" yet; purple-700 on a 10% tint is AA.
  scheduled: {
    text: "text-purple-700",
    bg: "bg-purple-500/10",
    border: "border-purple-500/30",
    accent: "border-l-purple-500",
    icon: "text-purple-700",
  },
  neutral: {
    text: "text-text-secondary",
    bg: "bg-gray-50",
    border: "border-gray-300",
    accent: "border-l-gray-400",
    icon: "text-text-tertiary",
  },
};

/** Convenience: classes for a status string. */
export function statusToneClasses(
  status: string | null | undefined,
): StatusToneClasses {
  return STATUS_TONE_CLASSES[statusTone(status)];
}
